import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { SearchType, SubscriptionType, UserRole } from '@spellion/shared';
import { useStore, useConfig } from '../store';
import { QuotaIndicator } from '../components/search';
import { useTranslation } from '../hooks/useTranslation';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * HomePage Component
 * 
 * Main landing page with word search functionality.
 * This is the primary entry point for users to search for words.
 * 
 * Features:
 * - Hero section with call to action
 * - Word search interface that redirects to search page (logged in users only)
 * - Login/Register prompt for anonymous visitors
 * - Feature highlights
 * 
 * @validates Requirements 1.1, 4.1, 10.1, 11.1, 11.3
 */
export function HomePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [searchType, setSearchType] = useState<SearchType>(SearchType.ANAGRAM);
  const isAuthenticated = useStore((state) => state.isAuthenticated);
  const token = useStore((state) => state.token);
  const subscriptionType = useStore((state) => state.subscriptionType);
  const userRole = useStore((state) => state.user?.role);
  
  // Quota state from store
  const quota = useStore((state) => state.quota);
  const setQuota = useStore((state) => state.setQuota);
  
  // Config state from store
  const freeUserDailyQuota = useStore((state) => state.freeUserDailyQuota);
  const loadPublicConfig = useStore((state) => state.loadPublicConfig);
  
  // Get pricing config
  const { premiumMonthlyPrice, premiumSubscriptionPrice } = useConfig();
  
  // Check if user is premium or admin
  const userIsPremium = subscriptionType === SubscriptionType.PREMIUM || userRole === UserRole.ADMIN;
  const userIsAdmin = userRole === UserRole.ADMIN;

  // Load public config on mount (for non-authenticated users to see correct quota value)
  useEffect(() => {
    loadPublicConfig();
  }, [loadPublicConfig]);

  // Load quota on mount for all authenticated users
  useEffect(() => {
    if (!isAuthenticated || !token) return;
    
    const fetchQuota = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/subscription/quota`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (data.success && data.data?.quota) {
          setQuota(data.data.quota);
        }
      } catch (error) {
        console.error('Failed to load quota:', error);
      }
    };
    
    fetchQuota();
  }, [isAuthenticated, token, setQuota]);

  /**
   * Handle search submission - redirect to search page with query
   */
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    const trimmedQuery = query.trim().toUpperCase();
    if (trimmedQuery.length > 0) {
      navigate(`/search?q=${encodeURIComponent(trimmedQuery)}&type=${searchType}`);
    }
  };

  /**
   * Handle input change - only allow valid characters (A-Z and *)
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    // Only allow A-Z and * (max 64 chars)
    const filtered = value.replace(/[^A-Z*]/g, '').slice(0, 64);
    setQuery(filtered);
  };

  /**
   * Get placeholder text based on search type
   */
  const getPlaceholder = () => {
    switch (searchType) {
      case SearchType.PATTERN:
        return t('home.search.placeholder.pattern');
      case SearchType.VERIFY:
        return t('home.search.placeholder.verify');
      default:
        return t('home.search.placeholder.anagram');
    }
  };

  return (
    <div className="space-y-12">
      {/* Hero Section */}
      <section className="text-center space-y-6 py-8 md:py-12">
        <div className="space-y-4">
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-slate-100">
            {t('home.hero.title')}
            <span className="block text-primary-500 dark:text-primary-400">{t('home.hero.titleHighlight')}</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            {t('home.hero.subtitle')}
          </p>
        </div>

        {/* Search section - different for authenticated vs anonymous users */}
        {isAuthenticated ? (
          /* Authenticated: Show functional search input with type selector */
          <form onSubmit={handleSearch} className="max-w-xl mx-auto space-y-3">
            {/* Search Type Selector */}
            <div className="flex justify-center gap-2">
              <button
                type="button"
                onClick={() => setSearchType(SearchType.ANAGRAM)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  searchType === SearchType.ANAGRAM
                    ? 'bg-primary-500 text-white'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                }`}
              >
                🔍 {t('home.search.types.anagram')}
              </button>
              <button
                type="button"
                onClick={() => setSearchType(SearchType.PATTERN)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  searchType === SearchType.PATTERN
                    ? 'bg-secondary-500 text-white'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                }`}
              >
                ✨ {t('home.search.types.pattern')}
              </button>
              <button
                type="button"
                onClick={() => setSearchType(SearchType.VERIFY)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  searchType === SearchType.VERIFY
                    ? 'bg-accent-500 text-white'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                }`}
              >
                ✓ {t('home.search.types.verify')}
              </button>
            </div>

            {/* Search Input */}
            <div className="flex gap-2 p-2 bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
              <input
                type="text"
                value={query}
                onChange={handleInputChange}
                placeholder={getPlaceholder()}
                className="flex-1 input border-0 focus:ring-0"
                aria-label={t('home.search.inputLabel')}
                maxLength={64}
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
              />
              <button 
                type="submit"
                className="btn-primary"
                disabled={query.trim().length === 0}
              >
                {t('home.search.button')}
              </button>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {searchType === SearchType.VERIFY 
                ? t('home.search.verifyHint')
                : t('home.search.hint')}
            </p>
            
            {/* Quota indicator for free users */}
            {!userIsPremium && !userIsAdmin && quota && (
              <div className="flex justify-center mt-4">
                <QuotaIndicator
                  remaining={quota.remaining}
                  total={quota.limit}
                  isPremium={false}
                  showUpgradeLink={false}
                />
              </div>
            )}
          </form>
        ) : (
          /* Anonymous: Show login/register prompt */
          <div className="max-w-xl mx-auto">
            <div className="p-6 bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="text-3xl">🔐</span>
                <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                  {t('home.auth.title')}
                </h2>
              </div>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                {t('home.auth.subtitle')}<br />
                <strong className="text-primary-600 dark:text-primary-400">{t('home.auth.freeSearches').replace('{count}', String(freeUserDailyQuota))}</strong>
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/register"
                  className="btn-primary px-6"
                >
                  {t('home.auth.createAccount')}
                </Link>
                <Link
                  to="/login"
                  className="btn-outline px-6"
                >
                  {t('home.auth.haveAccount')}
                </Link>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Features Section */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
        <div className="p-6 bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 transition-all duration-200 hover:shadow-md hover:-translate-y-1">
          <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/40 rounded-xl flex items-center justify-center text-2xl mb-4">
            🔍
          </div>
          <h3 className="font-display font-semibold text-lg text-slate-900 dark:text-slate-100 mb-2">
            {t('home.features.anagram.title')}
          </h3>
          <p className="text-slate-600 dark:text-slate-400">
            {t('home.features.anagram.description')}
          </p>
        </div>

        <div className="p-6 bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 transition-all duration-200 hover:shadow-md hover:-translate-y-1">
          <div className="w-12 h-12 bg-secondary-100 dark:bg-secondary-900/40 rounded-xl flex items-center justify-center text-2xl mb-4">
            ✨
          </div>
          <h3 className="font-display font-semibold text-lg text-slate-900 dark:text-slate-100 mb-2">
            {t('home.features.pattern.title')}
          </h3>
          <p className="text-slate-600 dark:text-slate-400">
            {t('home.features.pattern.description')}
          </p>
        </div>

        <div className="p-6 bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 transition-all duration-200 hover:shadow-md hover:-translate-y-1">
          <div className="w-12 h-12 bg-accent-100 dark:bg-accent-900/40 rounded-xl flex items-center justify-center text-2xl mb-4">
            ✓
          </div>
          <h3 className="font-display font-semibold text-lg text-slate-900 dark:text-slate-100 mb-2">
            {t('home.features.verify.title')}
          </h3>
          <p className="text-slate-600 dark:text-slate-400">
            {t('home.features.verify.description')}
          </p>
        </div>
      </section>

      {/* CTA Section - only show for non-authenticated users */}
      {!isAuthenticated && (
        <section className="text-center py-8 md:py-12 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-3xl text-white">
          <div className="max-w-2xl mx-auto px-6 space-y-4">
            <h2 className="font-display text-2xl md:text-3xl font-bold">
              {t('home.cta.title')}
            </h2>
            <p className="text-primary-100">
              {t('home.cta.subtitle').replace('{count}', String(freeUserDailyQuota))}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Link
                to="/register"
                className="btn bg-white text-primary-600 hover:bg-primary-50"
              >
                {t('home.auth.createAccount')}
              </Link>
              <Link
                to="/login"
                className="btn border-2 border-white text-white hover:bg-white/10"
              >
                {t('home.auth.haveAccount')}
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Premium CTA for authenticated free users (not for Premium or Admin) */}
      {isAuthenticated && !userIsPremium && !userIsAdmin && (
        <section className="text-center py-8 md:py-12 bg-gradient-to-r from-accent-500 to-accent-600 rounded-3xl text-white">
          <div className="max-w-2xl mx-auto px-6 space-y-4">
            <h2 className="font-display text-2xl md:text-3xl font-bold">
              {t('home.premiumCta.title')}
            </h2>
            <p className="text-accent-100">
              {t('home.premiumCta.subtitle')}
            </p>
            <Link
              to="/subscription"
              className="inline-block btn bg-white text-accent-600 hover:bg-accent-50"
            >
              {t('home.cta.premiumCta')}
            </Link>
          </div>
        </section>
      )}

      {/* Pricing Section - only show for non-authenticated users */}
      {!isAuthenticated && (
        <section className="py-8 md:py-12">
          <div className="text-center mb-8">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              {t('home.pricing.title')}
            </h2>
            <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              {t('home.pricing.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Free Plan */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 md:p-8 border border-slate-200 dark:border-slate-700 shadow-sm">
              <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                {t('home.pricing.free.title')}
              </h3>
              <p className="text-slate-500 dark:text-slate-400 mb-6">
                {t('home.pricing.free.description')}
              </p>
              <div className="text-4xl font-bold text-slate-900 dark:text-white mb-6">
                {t('home.pricing.free.price')}
              </div>
              <ul className="space-y-3 mb-8">
                <PricingFeature included>
                  {t('home.pricing.free.features.searches').replace('{count}', String(freeUserDailyQuota))}
                </PricingFeature>
                <PricingFeature included>{t('home.pricing.free.features.anagram')}</PricingFeature>
                <PricingFeature included>{t('home.pricing.free.features.pattern')}</PricingFeature>
                <PricingFeature included>{t('home.pricing.free.features.verify')}</PricingFeature>
                <PricingFeature>{t('home.pricing.free.features.unlimited')}</PricingFeature>
                <PricingFeature>{t('home.pricing.free.features.dictionaries')}</PricingFeature>
              </ul>
              <Link
                to="/register"
                className="block w-full py-3 text-center font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 hover:bg-primary-100 dark:hover:bg-primary-900/50 rounded-xl transition-colors"
              >
                {t('home.pricing.free.cta')}
              </Link>
            </div>

            {/* Premium Plan */}
            <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl p-6 md:p-8 text-white shadow-xl shadow-primary-200 dark:shadow-primary-900/50 relative overflow-hidden">
              <div className="absolute top-4 right-4 px-3 py-1 bg-amber-400 text-amber-900 text-xs font-bold rounded-full">
                {t('home.pricing.premium.badge')}
              </div>
              <h3 className="text-2xl font-bold mb-2">
                {t('home.pricing.premium.title')}
              </h3>
              <p className="text-primary-200 mb-6">
                {t('home.pricing.premium.description')}
              </p>
              
              {/* Dynamic pricing display */}
              <div className="mb-6">
                <div className="text-4xl font-bold">
                  {formatPrice(premiumSubscriptionPrice)} € <span className="text-lg font-normal text-primary-200">/ {t('home.pricing.premium.perMonth')}</span>
                </div>
                <div className="text-primary-200 text-sm mt-1">
                  {t('home.pricing.premium.subscription')}
                </div>
                <div className="text-primary-300 text-sm mt-2">
                  {t('home.pricing.premium.orOneTime').replace('{price}', formatPrice(premiumMonthlyPrice))}
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                <PricingFeature included light>{t('home.pricing.premium.features.unlimited')}</PricingFeature>
                <PricingFeature included light>{t('home.pricing.premium.features.anagram')}</PricingFeature>
                <PricingFeature included light>{t('home.pricing.premium.features.pattern')}</PricingFeature>
                <PricingFeature included light>{t('home.pricing.premium.features.verify')}</PricingFeature>
                <PricingFeature included light>{t('home.pricing.premium.features.dictionaries')}</PricingFeature>
                <PricingFeature included light>{t('home.pricing.premium.features.priority')}</PricingFeature>
              </ul>
              <Link
                to="/register"
                className="block w-full py-3 text-center font-medium text-primary-700 bg-white hover:bg-primary-50 rounded-xl transition-colors"
              >
                {t('home.pricing.premium.cta')}
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* MP Soft Solutions Banner - Full width, only for non-authenticated users */}
      {!isAuthenticated && (
        <section 
          className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 py-12 md:py-16 relative overflow-hidden"
          style={{
            width: '100vw',
            position: 'relative',
            left: '50%',
            right: '50%',
            marginLeft: '-50vw',
            marginRight: '-50vw',
            marginBottom: '-2.5rem',
          }}
        >
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-24 -left-24 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8 max-w-5xl mx-auto">
            {/* Text content */}
            <div className="text-center md:text-left max-w-xl">
              <div className="inline-flex items-center gap-2 bg-primary-500/20 text-primary-300 text-xs font-medium px-3 py-1 rounded-full mb-4">
                <SparkleIcon className="w-4 h-4" />
                {t('home.mpsoft.badge')}
              </div>
              <h3 className="text-2xl md:text-3xl font-display font-bold text-white mb-3">
                {t('home.mpsoft.title')}
              </h3>
              <p className="text-slate-300 mb-6">
                {t('home.mpsoft.description')}
              </p>
              <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                <a
                  href="https://www.mpsoftsolutions.com/#features"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-600 to-blue-600 hover:from-primary-500 hover:to-blue-500 text-white font-medium rounded-xl shadow-lg shadow-primary-500/25 transition-all hover:scale-105"
                >
                  {t('home.mpsoft.cta')}
                  <ArrowRightIcon className="w-4 h-4" />
                </a>
                <a
                  href="https://www.mpsoftsolutions.com/#products"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl border border-white/20 transition-all hover:scale-105"
                >
                  {t('home.mpsoft.ctaApps')}
                  <ArrowRightIcon className="w-4 h-4" />
                </a>
              </div>
            </div>
            
            {/* Services highlights */}
            <div className="grid grid-cols-2 gap-4 text-center">
              <ServiceBadge icon={<CodeIcon />} label={t('home.mpsoft.services.web')} />
              <ServiceBadge icon={<CogIcon />} label={t('home.mpsoft.services.custom')} />
              <ServiceBadge icon={<SparkleIcon className="w-6 h-6" />} label={t('home.mpsoft.services.modern')} />
              <ServiceBadge icon={<GlobeIcon />} label={t('home.mpsoft.services.websites')} />
            </div>
          </div>
        </div>
      </section>
      )}
    </div>
  );
}

// Helper function to format price
function formatPrice(price: number): string {
  return price.toFixed(2).replace('.', ',');
}

// Pricing Feature Component
interface PricingFeatureProps {
  children: React.ReactNode;
  included?: boolean;
  light?: boolean;
}

function PricingFeature({ children, included = false, light = false }: PricingFeatureProps) {
  return (
    <li className="flex items-center gap-3">
      {included ? (
        <CheckIcon className={light ? 'text-primary-200' : 'text-green-500'} />
      ) : (
        <XIcon className="text-slate-300 dark:text-slate-600" />
      )}
      <span className={included ? '' : 'text-slate-400 dark:text-slate-500 line-through'}>
        {children}
      </span>
    </li>
  );
}

// Service Badge Component
interface ServiceBadgeProps {
  icon: React.ReactNode;
  label: string;
}

function ServiceBadge({ icon, label }: ServiceBadgeProps) {
  return (
    <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-colors">
      <div className="text-primary-400 mb-2 flex justify-center">
        {icon}
      </div>
      <div className="text-white text-sm font-medium">{label}</div>
    </div>
  );
}

// Icons
function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={`w-5 h-5 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={`w-5 h-5 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function SparkleIcon({ className }: { className?: string }) {
  return (
    <svg className={className || 'w-4 h-4'} fill="currentColor" viewBox="0 0 24 24">
      <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
    </svg>
  );
}

function ArrowRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
    </svg>
  );
}

function CodeIcon() {
  return (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
    </svg>
  );
}

function CogIcon() {
  return (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}
